import React, { useEffect, useState } from 'react';
import {
  FunctionEntitySchema,
  QueueEntitySchema,
  EndpointEntitySchema,
  JoinedDiscoveryData,
  DiscoveryType,
} from '@hud/portal-dtos';
import { HudIcon, IconType } from '../../../../Icons/HudIcons';
import Skeleton from '../../../../Skeleton/Skeleton';
import ServiceBadge from '../../ServiceBadge/ServiceBadge';
import { Container } from './DiscoveryTable.styles';
import { Tabs } from '@mantine/core';
import { formatBigNumbers, formatDuration } from '../../../../utils';
import Entity from '../../Entity/Entity';

type EntityType = 'functions' | 'endpoints' | 'queues';
type EntityRow = FunctionEntitySchema | EndpointEntitySchema | QueueEntitySchema;

interface ExtractedStrings {
  id: number;
  service: string | null;
  serviceUrl: string;
  entity: string | null;
  entityUrl: string;
  type: string | null;
  runs: number | null;
  duration: number | null;
  trend: number | null;
  errorRate: number | null;
  icon: IconType;
}

const seeAllLinkMap = {
  endpoints: 'endpoints',
  queues: 'messageQueues',
  functions: 'functions',
};

// Map DiscoveryType to the correct orderBy parameter for "See all" links
function getOrderByForDiscoveryType(discoveryType: DiscoveryType): string {
  const orderByMap: Record<DiscoveryType, string> = {
    [DiscoveryType.Duration]: 'durationP90.value',
    [DiscoveryType.DurationTrend]: 'durationP90.trend',
    [DiscoveryType.ErrorRate]: 'errorRate.value',
    [DiscoveryType.ErrorRateTrend]: 'errorRate.trend',
    [DiscoveryType.Invocations]: 'runs.value',
    [DiscoveryType.InvocationsTrend]: 'runs.trend',
  };

  return orderByMap[discoveryType];
}

function formatErrorRate(value: number): React.ReactNode {
  const roundedValue = Math.round(value * 1000) / 10; // Round to 1 decimal places
  return `${roundedValue}%`;
}

function formatTrend(value: number): React.ReactNode {
  const isPositive = value > 0;
  const icon = isPositive ? 'designSystem.arrowUpRight' : 'designSystem.arrowDownRight';
  const className = isPositive ? 'up' : 'down';
  const sign = isPositive ? '+' : '';
  const roundedValue = Math.round(value * 1000) / 10; // Round to 1 decimal places
  const displayValue = `${sign}${roundedValue}%`;

  return (
    <div className={`trend ${className}`}>
      <span>{displayValue}</span>
      <HudIcon icon={icon} />
    </div>
  );
}

function extractStrings(
  row: EntityRow,
  entityType: EntityType,
  context: { globalFilters: string },
): ExtractedStrings {
  const { globalFilters } = context;

  const base = {
    id: row.id,
    service: null as string | null,
    serviceUrl: '/',
    entity: null as string | null,
    entityUrl: '/',
    type: null as string | null,
    runs: null as number | null,
    duration: null as number | null,
    trend: null as number | null,
    errorRate: null as number | null,
    icon: 'designSystem.cog' as IconType,
  };

  let extractedStrings;

  // Extract common fields based on entity type
  switch (entityType) {
    case 'functions': {
      const func = row as FunctionEntitySchema;
      extractedStrings = {
        ...base,
        id: func.id,
        entity: func.signature,
        entityUrl: `/explore/functions/${func.id}?${globalFilters}`,
        icon: 'designSystem.function' as IconType,
      };
      break;
    }
    case 'endpoints': {
      const endpoint = row as EndpointEntitySchema;
      extractedStrings = {
        ...base,
        id: endpoint.id,
        service: endpoint.serviceName,
        serviceUrl: `/explore/services/${endpoint.serviceId}?${globalFilters}`,
        entity: endpoint.path,
        entityUrl: `/explore/endpoints/${endpoint.id}-${endpoint.serviceId}?${globalFilters}`,
        icon: 'designSystem.endpoint' as IconType,
      };
      break;
    }
    case 'queues': {
      const queue = row as QueueEntitySchema;
      extractedStrings = {
        ...base,
        id: queue.id,
        entity: queue.queueName,
        entityUrl: `/explore/messageQueues/${queue.id}?${globalFilters}`,
        service: queue.serviceName,
        serviceUrl: `/explore/services/${queue.serviceId}?${globalFilters}`,
        type: queue.queueType,
        icon: 'designSystem.queue' as IconType,
      };
      break;
    }
  }
  if ('runs' in row && typeof row.runs === 'number') {
    extractedStrings.runs = row.runs;
  }

  if ('duration' in row && typeof row.duration === 'number') {
    extractedStrings.duration = row.duration;
  }

  if ('trend' in row && typeof row.trend === 'number') {
    extractedStrings.trend = row.trend;
  }

  if ('errorRate' in row && typeof row.errorRate === 'number') {
    extractedStrings.errorRate = row.errorRate;
  }

  return extractedStrings;
}

function TableBody({
  data,
  globalQueryString,
  entityType,
  shouldShowServices,
}: {
  entityType: EntityType;
  globalQueryString?: string;
  data: JoinedDiscoveryData;
  shouldShowServices: boolean;
}) {
  const globalFilters = globalQueryString ?? '';

  return data[entityType].map((row) => {
    const rowData = extractStrings(row, entityType, { globalFilters });
    return (
      <tr key={`${rowData.type}-${rowData.id}-${rowData.service}`}>
        {rowData.service !== null && shouldShowServices && (
          <td className="service">
            <ServiceBadge size="medium" name={rowData.service} url={rowData.serviceUrl} />
          </td>
        )}
        <td className="entity">
          <Entity entity={row} entityUrl={rowData.entityUrl} />
        </td>
        {rowData.runs !== null && (
          <td className="invocations">
            <div>{formatBigNumbers(rowData.runs)} Runs
            </div>
          </td>
        )}
        {rowData.duration !== null && (
          <td className="duration">
            <div>{formatDuration(rowData.duration)}</div>
          </td>
        )}
        {rowData.errorRate !== null && (
          <td className="error-rate">
            <div>{formatErrorRate(rowData.errorRate)}</div>
          </td>
        )}
        {rowData.trend !== null && (
          <td className="trend">{formatTrend(rowData.trend)}</td>
        )}
      </tr>
    );
  });
}

type DiscoveryTableProps = {
  globalQueryString?: string;
  data: JoinedDiscoveryData;
  discoveryType: DiscoveryType;
  isLoading: boolean;
  shouldShowServices: boolean;
};

export default function DiscoveryTable({
  data,
  globalQueryString,
  discoveryType,
  isLoading,
  shouldShowServices,
}: DiscoveryTableProps) {
  const [entityType, setEntityType] = useState<EntityType>('endpoints');

  // Build see all link with appropriate orderBy parameter
  const orderByParam = getOrderByForDiscoveryType(discoveryType);
  const globalQueryStringWithOrderBy = globalQueryString
    ? `${globalQueryString}&orderBy=${orderByParam}`
    : `orderBy=${orderByParam}`;
  const seeAllLink = `/explore/${seeAllLinkMap[entityType]}?${globalQueryStringWithOrderBy}`;

  const hasData = data[entityType].length > 0;

  // show the first entity type with data when the component mounts
  useEffect(() => {
    if (data.endpoints.length > 0) {
      setEntityType('endpoints');
    } else if (data.queues.length > 0) {
      setEntityType('queues');
    } else if (data.functions.length > 0) {
      setEntityType('functions');
    }
  }, [data]);

  return (
    <Container>
      <Tabs value={entityType} onChange={(value) => setEntityType(value as EntityType)}>
        <Tabs.List>
          <Tabs.Tab value="endpoints">Endpoints</Tabs.Tab>
          <Tabs.Tab value="queues">Queues</Tabs.Tab>
          <Tabs.Tab value="functions">Functions</Tabs.Tab>
          <a className="seeAll" href={seeAllLink}>See all</a>
        </Tabs.List>

        {isLoading ? (
          <Loading />
        ) : hasData ? (
          <table className="table">
            <tbody>
              <TableBody
                data={data}
                entityType={entityType}
                globalQueryString={globalQueryString}
                shouldShowServices={shouldShowServices}
              />
            </tbody>
          </table>
        ) : (
          <NoData discoveryType={discoveryType} />
        )}
      </Tabs>
    </Container>
  );
}

function Loading() {
  return (
    <table className="table loadingContainer">
      <tbody>
        <tr><td><Skeleton height={27} /></td></tr>
        <tr><td><Skeleton height={27} /></td></tr>
        <tr><td><Skeleton height={27} /></td></tr>
      </tbody>
    </table>
  );
}

function NoData({
  discoveryType,
}: {
  discoveryType: DiscoveryType;
}) {
  let noDataMessage: string | React.ReactNode = '';

  switch (discoveryType) {
    case DiscoveryType.Duration:
      noDataMessage = `No invocations detected for your selection, try changing the timeframe, environment, or triggering your service.`;
      break;
    case DiscoveryType.DurationTrend:
      noDataMessage = `No increasing durations detected - things are looking stable.`;
      break;
    case DiscoveryType.ErrorRate:
      noDataMessage = `No errors detected for your selection.`;
      break;
    case DiscoveryType.ErrorRateTrend:
      noDataMessage = `No rising error rates — things are looking stable.`;
      break;
    case DiscoveryType.Invocations:
      noDataMessage = `No invocations detected for your selection, try changing the timeframe, environment, or triggering your service.`
      break;
    case DiscoveryType.InvocationsTrend:
      noDataMessage = `No increase in run volume - traffic is steady.
`;
      break;
  }

  return (
    <div className="noData">
      <p>{noDataMessage}</p>
    </div>
  );
}
